import type { IResponseIntegrationWebhook } from 'entities/Webhook'
import {
  type IResponseContact,
  type IResponseContactHubspotIntegration,
  type IResponsePhones,
} from 'entities/Contacts/api/types'
import type { IResponseFilters } from 'entities/Contacts/api/filterTypes'
import { type IResponseFilterSegment } from 'entities/Segment'
import { type IAmpersandSettings } from 'entities/Ampersand'

export type IResponseUserCredential = {
  id: number
  user_id: number
  provider: string
  provider_user_id: string
  provider_identifier: string
  is_active: boolean
  nickname: string
  name: string
  email: string
  avatar: null
  third_token: null
  meta: {
    appUrl: string
    name: string
    email: string
    lang: number
    locale: string
    timezone_name: string
    timezone_offset: string
    default_currency: string
    icon_url: null
    active_flag: boolean
    hub_domain?: string
    is_admin: number
    role_id: number
    created: string
    has_created_company: boolean
    is_you: boolean
    access: Array<{
      app: string
      admin: boolean
      permission_set_id: string
    }>
    phone: null
    last_login: string
    modified: string
    company_id: number
    company_name: string
    company_domain: string
    company_country: string
    language: {
      language_code: string
      country_code: string
    }
    text_activity_key_string: string
  }
  expires_at: string
  created_at: string
  updated_at: string
  deleted_at: null
}

export enum IntegrationUnifiedKey {
  zoho = 'zoho',
  apollo = 'apollo',
  asana = 'asana',
  aha = 'aha',
  avoma = 'avoma',
  blueshift = 'blueshift',
  braze = 'braze',
  breakcold = 'breakcold',
  brevo = 'brevo',
  campaignMonitor = 'campaignMonitor',
  capsule = 'capsule',
  chilipiper = 'chilipiper',
  clariCopilot = 'clariCopilot',
  close = 'close',
  constantContact = 'constantContact',
  dixa = 'dixa',
  dynamicsBusinessCentral = 'dynamicsBusinessCentral',
  dynamicsCRM = 'dynamicsCRM',
  flatfile = 'flatfile',
  freshdesk = 'freshdesk',
  front = 'front',
  gong = 'gong',
  groove = 'groove',
  helpScoutMailbox = 'helpScoutMailbox',
  heyreach = 'heyreach',
  insightly = 'insightly',
  instantly = 'instantly',
  iterable = 'iterable',
  kit = 'kit',
  klaviyo = 'klaviyo',
  lemlist = 'lemlist',
  lever = 'lever',
  mixmax = 'mixmax',
  netsuite = 'netsuite',
  outreach = 'outreach',
  salesloft = 'salesloft',
  serviceNow = 'serviceNow',
  smartlead = 'smartlead',
  stripe = 'stripe',
  zendeskChat = 'zendeskChat',
  zendeskSupport = 'zendeskSupport',
  attio = 'attio',
  zoom = 'zoom',
  drift = 'drift',
}

export enum IntegrationKey {
  salesmessage = 'salesmessage',
  salesmsg = 'salesmsg',
  hubspot = 'hubspot',
  activecampaign = 'activecampaign',
  infusionsoft = 'infusionsoft',
  pipedrive = 'pipedrive',
  salesforce = 'salesforce',
  marketo = 'marketo',
  aircall = 'aircall',
  slack = 'slack',
  zipwhip = 'zipwhip',
  intercom = 'intercom',
  zapier = 'zapier',
  webhook = 'webhook',
  make = 'make',
  google = 'google',

  calendly = 'calendly',
  integrately = 'integrately',
  keragon = 'keragon',
  n8n = 'n8n',

  // ampersand
  zoho = 'zoho',
  apollo = 'apollo',
  asana = 'asana',
  aha = 'aha',
  avoma = 'avoma',
  blueshift = 'blueshift',
  braze = 'braze',
  breakcold = 'breakcold',
  brevo = 'brevo',
  campaignMonitor = 'campaignMonitor',
  capsule = 'capsule',
  chilipiper = 'chilipiper',
  clariCopilot = 'clariCopilot',
  close = 'close',
  constantContact = 'constantContact',
  dixa = 'dixa',
  dynamicsBusinessCentral = 'dynamicsBusinessCentral',
  dynamicsCRM = 'dynamicsCRM',
  flatfile = 'flatfile',
  freshdesk = 'freshdesk',
  front = 'front',
  gong = 'gong',
  groove = 'groove',
  helpScoutMailbox = 'helpScoutMailbox',
  heyreach = 'heyreach',
  insightly = 'insightly',
  instantly = 'instantly',
  iterable = 'iterable',
  kit = 'kit',
  klaviyo = 'klaviyo',
  lemlist = 'lemlist',
  lever = 'lever',
  mixmax = 'mixmax',
  netsuite = 'netsuite',
  outreach = 'outreach',
  salesloft = 'salesloft',
  serviceNow = 'serviceNow',
  smartlead = 'smartlead',
  stripe = 'stripe',
  zendeskChat = 'zendeskChat',
  zendeskSupport = 'zendeskSupport',
  attio = 'attio',
  zoom = 'zoom',
  drift = 'drift',
  agentforce = 'agentforce',
  google_sheet = 'google_sheet',
  google_doc = 'google_doc',
  google_calendar = 'google_calendar',
  salesmsg_mcp = 'salesmsg_mcp',
  'twilio-integration' = 'twilio-integration',
}

export enum IntegrationWebhookFeatureKey {
  phone = 'integration-webhook-phone-field',
  contact = 'integration-webhook-contact-owner',
}

export type IIntegrationKey = keyof typeof IntegrationKey

export type IResponseIntegration = {
  id: number
  key: IIntegrationKey
  is_active: boolean
  name: string
  description: string
  created_at: string
  updated_at: string
  photo_url: string
  oauth: boolean
  can_modify: boolean
  connected?: IResponseUserIntegration
  disconnected?: IResponseUserIntegration
  is_ampersand: boolean
  is_unified_connection: boolean
}
export type IResponseIntegrationShort = Omit<IResponseIntegration, 'connected' | 'disconnected'>

export type ICrmUserMeta = {
  username: string
  phone: string
  signature: string
  lang: string
  localZoneid: string
  mfaEnabled: string
  links: {
    lists: string
    userGroup: string
    dealGroupTotals: string
    dealGroupUsers: string
    configs: string
    dealConnection: string
    userConversationsPermission: string
    seatUser: string
  }
  id: string
}

export type ICrmUserIntegrationMeta = {
  id: string
  type: string
  userId: number
  userIdIncludingInactive: number
  createdAt: string
  updatedAt: string
  archived: boolean
  avatar?: string
  teams:
    | null
    | {
        id: string
        name: string
        primary: boolean
      }[]
}

export type ICrmUser<Meta = ICrmUserMeta> = {
  id: number
  integration_id: number
  user_integration_id: number
  user_id: number
  mapped_user_id?: number
  crm_user_id: string
  crm_owner_id: null | string
  organization_id: number
  credential_id: number
  crm_id: string
  first_name: string
  last_name: string
  email: string
  meta: Meta | null
  created_at: string
  updated_at: string
  invitation?: {
    email: string
    id: string
    user_id: number
  }
  avatar_url?: string
  has_duplicates?: boolean
  external_user_id?: string
  health?: IResponseIntegrationStateAccountHealth
}

export type IResponseUserIntegration = {
  id: number
  user_id: number
  organization_id: number
  integration_id: number
  user_credential_id: number
  provider_user_id: string | null
  is_active: boolean
  url: string
  api_key: string | null
  client_id: null
  client_secret: null
  token: null
  sync_token: string
  webhook_id: string
  expires_at: null
  connection_error: number
  last_check_at: string
  created_at: string
  updated_at: string
  deleted_at: null
  identity: string
  webhook_url: string
  webhook: IResponseIntegrationWebhook
  integration: IResponseIntegrationShort
  crm_users: ICrmUser[]
  user_credential?: IResponseUserCredential
}

export type IParamsIntegrations = {
  category?: string
  search?: string
}

export type IResponseIntegrations = {
  data: IResponseIntegration[]
}

export type IResponseIntegrationCategory = {
  data: string[]
}

export type IResponseCheckIntegrationConnectById =
  | {
      message: 'not_connected'
    }
  | IResponseUserIntegration

export type IResponseIntegrationDisconnected = {
  userIntegration: IResponseUserIntegration
  notifyUser: boolean
}

export type IResponseIntegrationRefreshed = {
  user: number
  integration_user: IResponseUserIntegration
}

export type IResponseIntegrationListSyncFinished = {
  list: IResponseHubspotList
}

export type IConnectTwilioParams = {
  account_sid?: string
  auth_token?: string
  api_key?: string
  api_key_secret?: string
  confirm_account_switch?: boolean
}

export type IResponseConnectTwilio = {
  success: boolean
}

export type IResponseIntegrationContactMatch = {
  id: string
  first_name: string | null
  last_name: string | null
  email: string | null
  full_name: string | null
  numbers: IResponsePhones
  hubspot_team_id: string | null
  photo_url: string | null
}

export type IResponseIntegrationContactsMatchingSearch = {
  current_page: number
  data: IResponseIntegrationContactMatch[]
  first_page_url: string
  from: number
  next_page_url: null
  path: string
  per_page: number
  prev_page_url: null
  to: number
}

type IEmptyMergeFieldType = ''

export type IMergeFieldType =
  | 'text'
  | 'number'
  | 'url'
  | 'date'
  | 'string'
  | 'enumeration'
  | 'datetime'
  | 'double'
  | 'phone'
  | 'address'
  | 'varchar'
  | 'single_select'
  | 'multi_select'
  | IEmptyMergeFieldType

export type IResponseMergeField = {
  updatedAt: string
  createdAt: string
  name: string
  label: string
  type: IMergeFieldType
  fieldType: string
  description: string
  groupName: string
  options: never[]
  displayOrder: number
  calculated: boolean
  externalOptions: boolean
  hasUniqueValue: boolean
  hidden: boolean
  hubspotDefined: boolean
  modificationMetadata: {
    archivable: boolean
    readOnlyDefinition: boolean
    readOnlyValue: boolean
  }
  formField: boolean
  value: string
  photo_url: string
  integrationKey: string
  key: string
  integrationId: number
  template: string
  referencedObjectType?: string
  dataSensitivity?: string
}

export type IIntegrationTagCreateDTO = {
  contact_integration_id: string | number
  tag: string
}

export type IIntegrationLinkDTO = {
  integration_id: number
  contact_id: number
  contact_integration_id: number | string
  first_name: string
  last_name: string
  email: string
  number: string
  update_contact: boolean
  integration_name?: string
  integration_key?: string
  phone_type?: string
}

export type IIntegrationSearchMatchingContactsParams = {
  integration_id: number
  query: string
  field: string
}

export type IResponseInfusionsoftTags = [
  {
    text: string
    value: string
    category: string
  }
]

export type IParamsIntegrationsContactsSearch = {
  term?: string
  per_page?: number
  page: number
}

export type IDeal = {
  id: {
    value?: number | null
    label: string | null
  }
  dealname: {
    value: string | null
    label: string | null
  }
  amount?: {
    value: string | number | null
    label: string | null
  }
  closedate?: {
    value: string | null
    label: string | null
  }
  dealstage?: {
    value: string | null
    label: string | null
  }
  pipeline?: {
    value?: string | null
    label: string | null
  }
  owner?: {
    value?: string | null
    first_name?: string
    last_name?: string
    label: string | null
  }
}

// Integration Activecampaign
export type IResponseActiveCampaignProperties = {
  first_name: string
  last_name: string
  email: string
  tags: string[]
}

export type IResponseActiveCampaignAccounts = {
  id: string
  name: string
}

export type IResponseActiveCampaignDeal = {
  title: string
  value: string
  owner: {
    id: string
    username: string
    first_name: string
    last_name: string
    email: string
    last_login: string
    sourceid: string
    sourceupdated: null
    productset: string
    a_now: string
    groups: string
    phone: string
  }
}

export type IResponseIntegrationsContactsActiveCampaignInfo = {
  properties: IResponseActiveCampaignProperties
  accounts: IResponseActiveCampaignAccounts
  deals: IResponseActiveCampaignDeal[]
}

export type IResponseIntegrationActivecampaignTagsApply = {
  status: string
  message: string
}

export type IResponseErrorIntegrationActivecampaignTagsApply = {
  contact_integration_id: string[]
  tag: string[]
}

export type IParamsIntegrationActivecampaignTagsApply = {
  contact_integration_id: string | number
  tag: string
}

// Integrations Infusionsoft
export type IResponseIntegrationInfusionsoftTagsApply = {
  status: string
  message: string
}

export type IResponseErrorIntegrationInfusionsoftTagsApply = {
  contact_integration_id: string[]
  tag: string[]
}

export type IParamsIntegrationInfusionsoftTagsApply = {
  contact_integration_id: string | number
  tag: string
}

export type IResponseInfusionsoftInfoCompany =
  | {
      id: number
      company_name: string
    }
  | []

export type IResponseInfusionsoftInfoOpportunities = {
  title: string
  opportunity_notes: string
  projected_revenue_low: number
  projected_revenue_high: number
  stage: {
    id: number
    name: string
    details: {
      stage_order: number
      target_num_days: number
      check_list_items: []
    }
    reasons: []
  }
  owner: {
    id: number
    first_name: null
    last_name: null
  }
}[]

export type IResponseInfusionsoftInfoProperties = {
  first_name: string
  last_name: string
  email: [
    {
      email: string
      field: string
    }
  ]
  tags: [
    {
      tag: {
        id: number
        name: string
        category: null
      }
      date_applied: string
    }
  ]
}

export type IResponseInfusionsoftInfo = {
  company: IResponseInfusionsoftInfoCompany
  opportunities: IResponseInfusionsoftInfoOpportunities
  properties: IResponseInfusionsoftInfoProperties
}

// Integration Hubspot
export type IResponseIntegrationHubspotProperties = {
  [key: 'firstname' | 'email' | 'lastname' | string]: {
    label: string
    value: string | null
  }
}

export type IResponseIntegrationHubspotDeal = IDeal

export type IResponseIntegrationHubspotCompany =
  | {
      id: {
        label?: string
        value: string
      }
      name: {
        value: string
        label: string | null
      }
      hubspot_owner: {
        value: {
          portalId: number
          ownerId: number
          type: string
          firstName: string
          lastName: string
          email: string
          createdAt: number
          updatedAt: number
          remoteList: [
            {
              id: number
              portalId: number
              ownerId: number
              remoteId: string
              remoteType: string
              active: boolean
            }
          ]
          hasContactsAccess: boolean
          activeUserId: null | string | number
          userIdIncludingInactive: null | string | number
          activeSalesforceId: null | string | number
          isActive: boolean
        }
        label: string | null
      }
    }
  | {
      [key in string]: {
        label: string | null
        value: string | null
      }
    }

export type IResponseIntegrationHubspotTicket = {
  id: {
    value: number
    label: string | null
  }
  subject: {
    value: string
    label: string | null
  }
}

export type IIntegrationHubspotPropertyFormatted = {
  key: string
  label: string
  value: string | null
}

export type IResponseIntegrationHubspotData = {
  companies: IResponseIntegrationHubspotCompany[]
  deals: IResponseIntegrationHubspotDeal[]
  properties: IResponseIntegrationHubspotProperties
  tickets: IResponseIntegrationHubspotTicket[]
  error?: {
    error: string
    type: IHubspotPropertiesObjectType
  }
}

export type IResponseIntegrationsContactsHubspotInfo = {
  data: IResponseIntegrationHubspotData
}

// Integration Pipedrive
export type IResponseIntegrationPipedriveOrganization = {
  name: string | null
  owner:
    | {
        id: number | string
        name: string | null
        email: string | null
        value: number | string | null
      }
    | null
    | []
}

export type IResponseIntegrationPipedriveDeal = {
  title: string
  value: number
  owner: {
    id: number
    name: string
    email: string
    has_pic: number
    pic_hash: string
    active_flag: boolean
    value: number
  }
  stage: {
    id: number
    order_nr: number
    name: string
    active_flag: boolean
    deal_probability: number
    pipeline_id: number
    rotten_flag: boolean
    rotten_days: number
    add_time: string
    update_time: string
  }
  pipeline: {
    id: number
    name: string
    url_title: string
    order_nr: number
    active: boolean
    deal_probability: boolean
    add_time: string
    update_time: string
    selected: boolean
  }
}

export type IResponseIntegrationPipedriveProperty = {
  first_name: string
  last_name: string
  email: string
}

export type IResponseIntegrationsContactsPipedriveInfo = {
  organization: IResponseIntegrationPipedriveOrganization | null
  deal: IResponseIntegrationPipedriveDeal[]
  properties: IResponseIntegrationPipedriveProperty | null
}

// Integration Salesforce
export type IResponseSalesforceProperty = {
  first_name: string
  last_name: string
  email: string
  object_type: 'contact' | 'lead'
}

export type IResponseSalesforceOpportunity = {
  id: string
  name: string | null
  stage: string | null
  value: number | null
}

export type IResponseIntegrationsContactsSalesforceInfo = {
  opportunities: IResponseSalesforceOpportunity[]
  properties: IResponseSalesforceProperty | null
}

export type IResponseHubspotList = {
  id: number
  list_id: number
  size: number | null
  name: string
  organization_id: number
  synced_at: string
  status: string | null
  sync_started_at: string | null
  updated_at: string | null
}

export type IResponseGetHubspotContactsList = {
  name: string
  listId: number
  size: number
  type: string
  teams: number[]
}

export type IParamsSetHubspotContactsLists = {
  lists: {
    id: number
    name: string
    size: number
  }[]
}

export type IParamsIntegrationsByKeyFields = IIntegrationKey

export type IParamsIntegrationsByKeyFieldsOptions = {
  onlyForMapping?: boolean
  customFields?: boolean
}

export type IParamsIntegration = {
  integration_id: number | string | null
  service_property: string | null
}

export type IParamsIntegrationContact = {
  contact_integration_id: string | number
  external_source: string
}

type ILinkedIntegrationContact = {
  notice: 'contact_linked' | 'contact_created'
  type: 'contact'
  data: IResponseContact
}

type IUnLinkedIntegrationContact = {
  notice: 'contact_linked' | 'contact_created'
  type: 'integration_contact'
  data: Array<{
    label: string
    name: string
    value: string
  }>
}

export type IResponseIntegrationContact = ILinkedIntegrationContact | IUnLinkedIntegrationContact

export type IParamsSearchAssociatedIntegrationContact = {
  term?: string
  per_page?: number
  dealId?: string
  ticketId?: string
  companyId?: string
  opportunityId?: string
  showAllUsers?: boolean
  after?: string | null
}

export type ISearchAssociatedIntegrationContact =
  | IResponseContactHubspotIntegration
  | IResponseContact

export type IResponseSearchAssociatedIntegrationContact = {
  results: Array<ISearchAssociatedIntegrationContact>
  total: number
  paging: {
    next: {
      after: string
    }
  }
}

export type IParamsGetHubspotExtensionUserDetect = {
  provider_identifier: string
  userId?: string
}

export type IResponseGetHubspotExtensionUserDetect = {
  status: 'can_request' | 'no_such_integration' | 'this_is_owner'
  owner: {
    id: number
  }
}

export type IParamsCreateHubspotExtensionInvitationRequest = {
  owner_id: number
  userId: string
}

export type IResponseCreateHubspotExtensionInvitationRequest = {
  error: string | 'already_requested'
  ok: boolean
}

export type IResponseIntegrationMeeting = {
  link: string
  name: string
  id: string
  slug: string
  type: string
  organizerUserId: string
  userIdsOfLinkMembers: string[]
  defaultLink: boolean
  createdAt: string
  updatedAt: string
}

export type IResponseCalendlyIntegrationMeeting = {
  id: string
  name: string
  scheduling_url: string
  duration: number
  active: boolean
  additional_fields: IResponseAdditionalField[]
  is_configuration_valid: boolean
}

export type IResponseGetCalendlyIntegrationMeetings = {
  items: Array<IResponseCalendlyIntegrationMeeting>
  pagination: {
    next_page_token: string
    previous_page_token: string
  }
}

export type IResponseAdditionalFieldType = 'text' | 'single_select' | 'multi_select' | string

export type IResponseAdditionalField = {
  default: boolean
  key: string
  name: string
  required: boolean
  type: IResponseAdditionalFieldType
  choices: string[]
}

export type IResponseGetIntegrationMeetingsFields = {
  id: string
  name: string
  active: boolean
  additional_fields: IResponseAdditionalField[]
}

export type IParamsCalendlyIntegrationMeetings = {
  limit: number
  is_active: boolean
  page_token?: string
}

export type IResponseGetIntegrationMeetings = {
  data: Array<IResponseIntegrationMeeting>
  paging: null
  error?: string
}

export type IParamsConnectActiveCampaign = {
  integrationKey: IIntegrationKey
  apiUrl: string
  apiKey: string
}

type ResponseWrapper<Key extends string, Data> = {
  [key in Key]: Data
}

export type IIntegrationSettingsProperties = {
  custom_field_name: string
  integration_field_name: string
  integration_field_type: string
  integration_object_type?: string
}

export type IntegrationSettingsHubspotCheckboxes =
  | 'update_contacts'
  | 'create_non_existing_contacts'
  | 'update_owner'

export type IntegrationSettingsTimelineLogCheckboxes =
  | 'log_calls'
  | 'log_messages'
  | 'log_notes'
  | 'log_voicemails'

export type IHubspotSettingsDict = {
  update_contacts: BinaryNumber
  create_non_existing_contacts: BinaryNumber
  phone_number_priority: string
  update_owner: BinaryNumber
  log_calls: BinaryNumber
  log_messages: BinaryNumber
  log_notes: BinaryNumber
  log_voicemails: BinaryNumber
  properties_mapping: IIntegrationSettingsProperties[]
}

export type ISalesForceSettingsDict = {
  enable_timelines: BinaryNumber
  update_contacts: BinaryNumber
  create_non_existing_contacts: BinaryNumber
  phone_number_priority: string
  new_record_type: string
  default_contact_account: string
  is_custom_phone_number_enabled: BinaryNumber
  lead_custom_phone_number: string
  contact_custom_phone_number: string
  conversation_assignment_enabled: BinaryNumber
  properties_mapping: IIntegrationSettingsProperties[]
}

export type ISettingRequestBySettingDict<T> = Array<
  {
    [K in keyof T]: { key: K; value: T[K] }
  }[keyof T]
>

export type IParamsSettingDict_1_0_Value = {
  custom_field_name: string
  integration_field_name: string
}

export type IParamsSettingDict_1_0 = {
  featureKey: string
  settingsKey: string
  value: boolean | IParamsSettingDict_1_0_Value[]
}

export type IResponseSettingDict_1_0 = {
  featureKey: string
  settingsKey: string
  value: boolean | IParamsSettingDict_1_0_Value[]
}

export type ISettingRequestOptionsBySettingDict<T> = {
  [key: string]: {
    [K in keyof T]: Array<{ label: string; value: T[K] }>
  }
}

export type IResponseGetIntegrationSettings<T> = ResponseWrapper<
  'data',
  ISettingRequestBySettingDict<T>
>

export type IHubspotPropertiesObjectType = 'contact' | 'deal' | 'company' | 'ticket'

export type ISalesforcePropertiesObjectType = 'contact' | 'lead'

export type IParamsPagination = {
  page: number
  limit: number
  query?: string
  search?: string
}

export type IResponsePagination<Data, Meta = undefined> = {
  current_page: number
  data: Data
  meta: Meta
  first_page_url: string
  from: number
  last_page: number
  last_page_url: string
  links: Array<{
    url: string
    label: string
    active: boolean
  }>
  next_page_url: string
  path: string
  per_page: number
  prev_page_url: null
  to: number
  total: number
}

export type IResponseGetIntegrationUsers = IResponsePagination<
  Array<ICrmUser<ICrmUserIntegrationMeta>>,
  {
    crm_owner_id: string
    total_items: number
    mapped: number
    unmapped: number
    total?: number
  }
>

export type IParamsPutIntegrationCalendarUserFieldValue = {
  mapped_user_id: number
}

export type IParamsPatchIntegrationUserFieldValue = {
  attribute: keyof ICrmUser<ICrmUserIntegrationMeta>
  value: string | number
}

type HubspotChannelMeta = {
  id: string
  name: string
  active: boolean
  inboxId: string
  archived: boolean
  channelId: string
  createdAt: string
  authorized: boolean
  channelAccountId: string
  deliveryIdentifier: {
    type: string
    value: string
  }
}

export type IIntegrationChannel = {
  id: number
  name: string
  hubspot_channel_meta: HubspotChannelMeta
}

export type IResponseGetIntegrationChannels = IResponsePagination<IIntegrationChannel[]>

export type IParamsGetTwilioAccount = {
  account_sid: string
  installation_sid: string
}

export type IResponseGetTwilioAccount = {
  id: number
  organization_id: number | null
  installation_id: string
  vendor_id: string
  is_active: boolean
  owner: {
    email: string
    first_name: string | null
    last_name: string | null
  }
}

export type ISalesforcePackageType = 'premium' | 'free' | 'sandbox'
export type IAgentforcePackageType = 'production' | 'sandbox'

export type IIntegrationOrganizationSettingType = ISalesforcePackageType | IAgentforcePackageType

export type IIntegrationOrganizationSetting =
  | { key: 'salesforce_package_type'; value: ISalesforcePackageType }
  | { key: 'agentforce_package_type'; value: IAgentforcePackageType }

export type IIntegrationOrganizationSettingsResponse = ResponseWrapper<
  'data',
  IIntegrationOrganizationSetting[]
>

export type IGetIntegrationVersionResponse = {
  id: number
  installation_url: string
  installation_url_sandbox: string
  version: string
}

export type IParamsSaveHubspotCallTypeByCallSid = {
  call_sid: string
  type: string
}

export type IParamsSaveHubspotOutcomeByCallSid = {
  call_sid: string
  outcome: string
}

export type IAutomationLogExecutionStatus = 'success' | 'failed'
export type IAutomationLogSource = 'hs_workflow'

export type IResponseAutomationLog = {
  id: number
  callback_id: string
  action: string
  entity_id: string
  entity_type: string
  contact_name: string | null
  contact_phone: string | null
  executed_at: string
  failed_reason: null | string
  payload: null | string
  status: IAutomationLogExecutionStatus
  step: string
  url: null | string
  result: IResponseAutomationLogResult
}

export type IResponseAutomationLogResult = {
  type: string | null
  title: string | null
}

export type IResponseAutomationLogs = {
  data: IResponseAutomationLog[]
  meta: {
    limit: number
    page: number
    total: number
  }
}

export type IResponseAutomationLogsFilters = IResponseFilters

export type IParamsAutomationLogsList = {
  limit: number
  page: number
  search: string
  sortOrder: string
  sortBy: string
  filters: IResponseFilterSegment[]
}

export type IResponseSaveHubspotCallTypeByCallSid = { success: boolean; error?: string }

export type IResponseGetHubspotActivityTypes = Array<string>

export type IResponseSaveHubspotOutcomeByCallSid = { success: boolean; error?: string }
export type IResponseGetHubspotOutcome = Array<{
  id: string
  label: string
}>

export type IResponseIntegrationStatePlan = 'basic' | 'teams' | 'standard'
export type IResponseIntegrationStateStatus = 'free' | 'trial' | 'paid'
export type IResponseIntegrationStateAccountHealth = {
  is_healthy: boolean
  requires_reconnect: boolean
  error: string | null
  checked_at: string | null
}
export type IResponseIntegrationStateAccount = {
  id: number
  organization_id: number
  external_organization_id: string
  external_user_id: string
  first_name: string
  last_name: string
  email: string
  avatar_url: string
  mapped_user_id: number | null
  created_at: string
  updated_at: string
  health: IResponseIntegrationStateAccountHealth
}
export type IResponseIntegrationState = {
  is_connected: boolean
  credentials: {
    organization_id: number
    user_id: number
    external_organization_id: string
    external_user_id: string
  } | null
  external_organization: {
    id: string
    name: string
    plan_name: IResponseIntegrationStatePlan
    status: IResponseIntegrationStateStatus
  } | null
  accounts?: IResponseIntegrationStateAccount[]
}

export type IGoogleCalendarAccessRole = 'owner' | 'writer' | 'reader' | 'freeBusyReader'

export type IParamsGetGoogleCalendarList = {
  account_id: string
  writable_only?: boolean
  meet_only?: boolean
  page_token?: string
  limit?: number
}

export type IResponseGoogleCalendarListItem = {
  id: string
  name: string
  account_id: string
  access_role: IGoogleCalendarAccessRole
  is_primary: boolean
  timezone: string
  meet_enabled: boolean
  is_bookable: boolean
}

export type IResponseGoogleCalendarList = {
  items: IResponseGoogleCalendarListItem[]
  pagination: {
    next_page_token: string
  }
}

export type IntegrationSettingsActiveCampaignCheckboxes =
  | 'update_contacts'
  | 'create_non_existing_contacts'

export type IActiveCampaignSettingsDict = {
  update_contacts: BinaryNumber
  create_non_existing_contacts: BinaryNumber
  phone_number_priority: string
  update_owner: BinaryNumber
  log_calls: BinaryNumber
  log_messages: BinaryNumber
  log_notes: BinaryNumber
  log_voicemails: BinaryNumber
  properties_mapping: IIntegrationSettingsProperties[]
}

export type IResponseGetZohoAppSettings = IAmpersandSettings

export type IResponseAutoMapIntegrationUsers = {
  success: string
  message: string
}

export type IResponseSlackChannel = {
  id: string
  name: string
}
export type IResponseSlackIntegrationChannels = {
  channels: IResponseSlackChannel[]
}

export type IResponseFrontChannel = {
  front_inbox: {
    id: string
    name: string
  } | null
  salesmsg_inbox: {
    id: number
    name: string
  } | null
  number: {
    id: number
    number: string
    formatted_number: string
  } | null
}
export type IResponseFrontIntegrationChannels = {
  channels: IResponseFrontChannel[]
}

export type IResponseSlackMapping = {
  mapping: {
    [channel_id: string]: number[]
  }
}

export type IParamsAddMessengerMapping = {
  integration: IIntegrationKey
  inboxes: number[]
  channel_id?: string
  channel_name?: string
  previous_channel_id?: string
}

export type IResponseMessengerMapping = {
  ok: boolean
}

export type IResponseGetIntegrationObjects = {
  read: string[]
  write: string[]
}

export type IIntegrationObjectSelectField = {
  field_key: string
  field_name: string
  required: boolean
  type: 'singleSelect' | 'multiSelect'
  hide_on?: Record<string, string[]>
  options: Array<{
    key: string
    value: string
  }>
}

export type IIntegrationObjectField =
  | {
      field_key: string
      field_name: string
      required: boolean
      type:
        | 'string'
        | 'url'
        | 'email'
        | 'boolean'
        | 'datetime'
        | 'date'
        | 'int'
        | 'float'
        | 'arrayOfStrings'
        | 'other'
    }
  | IIntegrationObjectSelectField

export type IResponseGetIntegrationObjectsMetadata = {
  object_name: string
  display_name: string
  fields: Array<IIntegrationObjectField>
}

export type IIntegrationsListResponse = {
  settingsPermit: boolean
  all: IResponseIntegrationShort[]
  userIntegrations: IResponseUserIntegration[]
  disconnected?: IResponseUserIntegration[]
}

export type IResponseGetConnectData = {
  data: {
    token: string
    connect_link: string
    expires_at: string
  }
}
